import React, { useEffect, useMemo, useState } from 'react'
import { MapContainer, Marker, TileLayer } from 'react-leaflet'

import { CompassOutlined } from '@ant-design/icons'
import { Avatar, Icon, Skeleton, Typography } from 'antd'

import { getLocationsWithContactsByCustomer, getReportInfoChart } from '../../../services/api'
import { request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'

import './Locals.css'

const DEFAULT_VIEWPORT = {
  center: [-15.79, -47.88],
  zoom: 4
}

const Locals = function ({ customer }) {
  const [collapsed, setCollapsed] = useState(true)
  const [locations, setLocations] = useState([])
  const [marker, setMarker] = useState([])
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT)
  const [representativeData, setRepresentativeData] = useState({
    itemCurrent: {
      chartType: ''
    },
    data: []
  })

  useEffect(() => {
    if (customer.id) {
      listCustomerLocations(customer.id)
      getChartRepresentativeData(customer)
    }
    setViewport(DEFAULT_VIEWPORT)
  }, [])

  async function listCustomerLocations(id) {
    if (!id) {
      return
    }
    try {
      const response = await request(getLocationsWithContactsByCustomer(id), true)
      setLocations(response)

      const dataCoordinates = response
        .filter((location) => location.latitude && location.longitude)
        .map((item) => ({ lat: item.latitude, long: item.longitude }))
      setMarker(dataCoordinates)
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function getChartRepresentativeData(params) {
    request(getReportInfoChart(params.id))
      .then((result) => {
        setRepresentativeData({ data: result })
      })
      .catch((error) => {
        console.tron.log('getChartRepresentativeData - error', error)
      })
  }

  const ContactLocations = ({ data }) => {
    const mainContact = data.contacts.find(({ isMain }) => isMain) || data.contacts[0]
    const targetChartData = representativeData.data.find(({ districtId }) => districtId === data.districtId)

    return (
      <div className="container-locals">
        <div key={data.id} className="locals">
          <Typography style={{ color: '#263447', fontSize: 14, fontWeight: 'bold', marginBottom: 20 }}>
            {data.name}
          </Typography>

          <div className="horizontal-line" style={{ height: 20 }} />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar className="gx-avatar gx-pointer avatar-contacts" alt="">
              <span className="avatar-initial-name">{data.name.substr(0, 2).toLocaleUpperCase()}</span>
            </Avatar>

            {mainContact && (
              <>
                <Typography style={{ color: '#263447', fontSize: 18, marginLeft: 12 }}>
                  {mainContact.name.split(/(\s+)/)[0]}
                </Typography>

                {mainContact.phoneNumber && (
                  <>
                    <div className="line-vertical" style={{ marginLeft: 12, height: 40 }} />
                    <Typography style={{ color: '#263447', marginLeft: 12 }}>{mainContact.phoneNumber}</Typography>
                  </>
                )}

                {mainContact.email && (
                  <>
                    <div className="line-vertical" style={{ marginLeft: 12, height: 40 }} />
                    <Typography style={{ color: '#263447', marginLeft: 12 }}>{mainContact.email}</Typography>
                  </>
                )}

                {mainContact.jobRole && (
                  <>
                    <div className="line-vertical" style={{ marginLeft: 12, height: 40 }} />
                    <Typography style={{ color: '#263447', marginLeft: 12 }}>{mainContact.jobRole}</Typography>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <ElectedResults chartData={targetChartData} />
      </div>
    )
  }

  const ElectedResults = ({ chartData }) => {
    if (!chartData) {
      return (
        <div className="container-elected-results">
          <Skeleton active />
        </div>
      )
    }

    if (chartData) {
      return (
        <div className="container-elected-results">
          <div className="representative-container">
            <Typography className="representative-label">
              {getMessage('representative') + chartData.district}
            </Typography>
            <div className="horizontal-line" />
            <div className="label-representative-data">
              <Typography className="representative-data">
                {isNaN(chartData.representativeDistrict.percent)
                  ? '0.00%'
                  : `${chartData.representativeDistrict.percent}%`}
              </Typography>
            </div>
          </div>

          <div className="representative-container">
            <Typography className="representative-label">{getMessage('representative') + chartData.region}</Typography>
            <div className="horizontal-line" />
            <div className="label-representative-data">
              <Typography className="representative-data">
                {isNaN(chartData.representativeRegion.percent) ? '0.00%' : `${chartData.representativeRegion.percent}%`}
              </Typography>
            </div>
          </div>

          <div className="representative-container">
            <Typography className="representative-label">{getMessage('nationalRepresentative')}</Typography>
            <div className="horizontal-line" />
            <div className="label-representative-data">
              <Typography className="representative-data">
                {isNaN(chartData.representativeNacional.percent)
                  ? '0.00%'
                  : `${chartData.representativeNacional.percent}%`}
              </Typography>
            </div>
          </div>
        </div>
      )
    }
  }

  const toggleCollapsed = () => setCollapsed((collapsed) => !collapsed)

  const nationalRepresentativePercent = useMemo(
    () =>
      Object.values(
        representativeData.data?.reduce((acc, item) => {
          // Is possible to have more than one representative in the same district with the same percent
          acc[item.districtId] = Number(item.representativeNacional.percent)
          return acc
        }, {})
      ).reduce((a, b) => a + b, 0),
    [representativeData?.data]
  )

  return (
    <div>
      <div style={{ padding: '2%' }}>
        <div className="default-header-div">
          <div className="default-header-breadcrumb">
            <Typography.Title className="default-header-title" level={3}>
              <Icon
                type={collapsed ? 'up' : 'down'}
                style={{ marginRight: 13, fontSize: 15 }}
                onClick={toggleCollapsed}
              />
              <CompassOutlined style={{ marginRight: 13 }} />
              {getMessage('locations')}
            </Typography.Title>

            <div style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}>
              <Typography style={{ color: '#263447', fontWeight: 'bold' }}>
                {getMessage('nationalRepresentative')}
              </Typography>
              <Typography style={{ color: '#04D1D1', fontWeight: 'bold', fontSize: 29 }}>
                {`${nationalRepresentativePercent.toFixed(2)}%`}
              </Typography>
            </div>
          </div>

          {collapsed ? (
            <div>
              <div style={{ display: 'flex' }}>
                <MapContainer
                  viewport={viewport}
                  animate={true}
                  scrollWheelZoom={false}
                  boxZoom={false}
                  doubleClickZoom={false}
                  center={viewport.center}
                  zoom={viewport.zoom}
                >
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.osm.org/{z}/{x}/{y}.png"
                  />
                  {marker.map((data, index) => {
                    return <Marker key={index} position={[data.lat, data.long]} zoom={data.zoom} />
                  })}
                </MapContainer>
              </div>

              <div className="contact-locations">
                {locations.map((location) => (
                  <ContactLocations key={location.id} data={location} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Locals
